import { useMemo } from 'react';
import { ClaimType, ClaimShopCarrier } from '../types';

export interface SelectOption {
  label: string;
  value: string;
}

interface UseClaimCarrierSelectOptionsParams {
  carrierCode?: string;
  carrierCountryCode?: string;
  carriers: ClaimShopCarrier[];
  type?: ClaimType;
}

interface Result {
  carrierOptions: SelectOption[];
  carrierCountryOptions: SelectOption[];
}

export function useClaimCarrierSelectOptions({
  carrierCode,
  carriers,
  type,
}: UseClaimCarrierSelectOptionsParams): Result {
  const filteredCarriers = useMemo(() => {
    if (type === 'cash_on_delivery') {
      return carriers.filter((carrier) => carrier.isNon7s);
    }

    return carriers;
  }, [carriers, type]);

  const carrierOptions = useMemo(
    () => getUniqueCarriers(filteredCarriers).map(({ name, code }) => ({ label: name || code, value: code })),
    [filteredCarriers]
  );

  const carrierCountryOptions = useMemo(() => getCarrierCountryOptions(carrierCode, filteredCarriers), [
    filteredCarriers,
    carrierCode,
  ]);

  return {
    carrierOptions,
    carrierCountryOptions,
  };
}

function getUniqueCarriers(carriers: ClaimShopCarrier[]): ClaimShopCarrier[] {
  return [...new Map(carriers.map((carrier) => [carrier.code, carrier])).values()];
}

function getCarrierCountryOptions(carrierCode: string | undefined, carriers: ClaimShopCarrier[]): SelectOption[] {
  return carriers
    .filter(({ code }) => code === carrierCode)
    .map((carrier) => ({ label: carrier.countryName, value: carrier.countryCode }));
}
